import React, { createContext, FC, useEffect, useMemo, useState, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { cloneDeep, isEqual, orderBy } from "lodash";
import { toast } from "react-toastify";
import BenchmarkingMedicaidService from "@app/overviews/network-benchmarking-dashboard/benchmarkingMedicaidService";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import { CompetitiveInsightsSelectedSortType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/competitiveInsightsSelectedSortType";
import { useMedicaidPrimaryFilterOptionUpdates } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/hooks/useMedicaidPrimaryFilterOptionUpdates";
import { useMedicaidPrimaryFilters } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/hooks/useMedicaidPrimaryFilters";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { MedicaidParentOrganization } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganization";
import { MedicaidParentOrganizationSortingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidParentOrganizationSortingTypeEnum";
import { MedicaidPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidPrimaryFiltersPayload";
import { NetworkProgram } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/NetworkProgram";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { UserContext } from "@app/user/UserContext";

export interface MedicaidFiltersContextValues {
  allPrograms: NetworkProgram[];
  applyingServiceArea: MedicaidNetwork[];
  handleSetSelectedSortType: (sortType: CompetitiveInsightsSelectedSortType) => void;
  onApplyPrimaryFiltersClick: () => void;
  onApplyServiceAreaClick: (countyIds: number[], contracts: BenchmarkingNetworkType[]) => void;
  onClearNetworkFiltersClick: () => void;
  onClearPrimaryFiltersClick: () => void;
  networkFiltersLoading: boolean;
  parentOrganizations: MedicaidParentOrganization[];
  primaryFiltersLoading: boolean;
  programs: NetworkProgram[];
  selectedMembershipCount: MembershipCountTypeEnum;
  selectedSortType: MedicaidParentOrganizationSortingTypeEnum;
  setSelectedMembershipCount: (selectedMembership: MembershipCountTypeEnum) => void;
  setSelectedSortType: (selectedSortType: MedicaidParentOrganizationSortingTypeEnum) => void;
  sortNetworks: (filteredParents: BenchmarkingParentOrganizationType[]) => BenchmarkingParentOrganizationType[];
}

const initialValues: MedicaidFiltersContextValues = {
  allPrograms: [],
  applyingServiceArea: [],
  handleSetSelectedSortType: () => null,
  networkFiltersLoading: false,
  onApplyPrimaryFiltersClick: () => null,
  onApplyServiceAreaClick: () => null,
  onClearNetworkFiltersClick: () => null,
  onClearPrimaryFiltersClick: () => null,
  parentOrganizations: [],
  primaryFiltersLoading: false,
  programs: [],
  selectedMembershipCount: MembershipCountTypeEnum.All,
  selectedSortType: null,
  setSelectedMembershipCount: () => null,
  setSelectedSortType: () => null,
  sortNetworks: () => null,
}

export const MedicaidFiltersContext = createContext(initialValues);

interface ApplyPrimaryFiltersVariables {
  applyingServiceArea: MedicaidNetwork[];
  primaryFilters: MedicaidPrimaryFiltersPayload;
}

const MedicaidFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [applyingServiceArea, setApplyingServiceArea] = useState<MedicaidNetwork[]>(initialValues.applyingServiceArea);
  const [allPrograms, setAllPrograms] = useState<NetworkProgram[]>([]);
  const [parentOrganizations, setParentOrganizations] = useState<MedicaidParentOrganization[]>(initialValues.parentOrganizations);
  const [selectedMembershipCount, setSelectedMembershipCount] = useState<MembershipCountTypeEnum>(initialValues.selectedMembershipCount);
  const [selectedSortType, setSelectedSortType] = useState<MedicaidParentOrganizationSortingTypeEnum>(null);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicaidService = useMemo(() => new BenchmarkingMedicaidService(), []);
  const { isPending: networkFiltersLoading, mutate: applyPrimaryFiltersMutation } = useMutation({
    mutationFn: ({ primaryFilters }: ApplyPrimaryFiltersVariables) => benchmarkingMedicaidService.getBenchmarkingNetworkData(primaryFilters),
    onSuccess: (data, variables) => {
      if (!data) return;

      const newParentOrganizations: MedicaidParentOrganization[] = [];
      if (!!data.yourNetwork) {
        newParentOrganizations.push({
          hContract: data.yourNetwork,
          parentOrganizationName: "Your QES Networks",
        });
      }

      newParentOrganizations.push(...data.comparisonNetworks);

      for (const parentOrg of newParentOrganizations) {
        parentOrg.hContract.parentOrganization = parentOrg.parentOrganizationName;
      }

      setParentOrganizations(newParentOrganizations);

      const newSelectedNetworks: MedicaidNetwork[] = [];

      const handlePreviousSelections = (serviceArea: BenchmarkingNetworkType): void => {
        const parentOrganization: MedicaidParentOrganization = newParentOrganizations.find(x => x.parentOrganizationName === serviceArea.parentOrganization);

        if (!!parentOrganization && !parentOrganization.hContract.disabled) {
          // the previously-selected contract must be re-acquired in order to obtain the latest map data
          newSelectedNetworks.push(parentOrganization.hContract);
        }
      }

      if (!!variables.applyingServiceArea.length) {
        // when applying service area
        toast("Service Area Updated", {
          containerId: "z-index-9999",
          type: "success",
        });

        for (const serviceArea of variables.applyingServiceArea) {
          if (!serviceArea.disabled) {
            handlePreviousSelections(serviceArea);
          }
        }

        if (benchmarkingFiltersContext.selectedTab !== CompetitiveInsightsTabTypeEnum.NetworkSelection) {
          benchmarkingFiltersContext.setSelectedTab(CompetitiveInsightsTabTypeEnum.NetworkSelection)
        }

        setApplyingServiceArea([]);
      } else {
        // when retaining networks from a previous search
        for (const previousSelectedNetwork of benchmarkingFiltersContext.previousSelectedNetworks) {
          if (newSelectedNetworks.length < 5) {
            handlePreviousSelections(previousSelectedNetwork);
          }
        }
      }

      benchmarkingFiltersContext.setSelectedNetworks(newSelectedNetworks);
    },
  });

  const { clientId } = useContext(UserContext);

  const programs = useMemo(
    () => allPrograms.filter(p => p.stateCode === benchmarkingFiltersContext.selectedState?.stateAbbreviation),
    [allPrograms, benchmarkingFiltersContext.selectedState]
  );

  const isMedicaid = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid;

  const { isLoading: primaryFiltersLoading, primaryFilterData } = useMedicaidPrimaryFilters(clientId, isMedicaid);

  const updatesStateId = benchmarkingFiltersContext.selectedState?.stateId;
  const { primaryFilterUpdates } = useMedicaidPrimaryFilterOptionUpdates(clientId, updatesStateId, benchmarkingFiltersContext.programKey, isMedicaid);

  useEffect(() => {
    if (!isMedicaid) return;
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCountyIdFilter(null);
    setSelectedMembershipCount(MembershipCountTypeEnum.All);
    benchmarkingFiltersContext.setProgramKey(null);
  }, [isMedicaid]);

  useEffect(() => {
    if (!primaryFilterData || !isMedicaid) return;
    benchmarkingFiltersContext.processSpecialties(primaryFilterData.specialties);
    benchmarkingFiltersContext.setStates(primaryFilterData.states);
    benchmarkingFiltersContext.setAffiliationAuthorizedStates(primaryFilterData.affiliationAuthorizedStates);
    setAllPrograms(primaryFilterData.programs ?? []);
  }, [primaryFilterData, isMedicaid]);

  useEffect(() => {
    benchmarkingFiltersContext.setCountyIdFilter(null);
    benchmarkingFiltersContext.setProgramKey(programs.length === 1 && !!programs[0]?.programKey ? programs[0].programKey : null);
  }, [benchmarkingFiltersContext.selectedState, programs]);

  useEffect(() => {
    if (!primaryFilterUpdates) return;
    benchmarkingFiltersContext.setMarketProjectId(primaryFilterUpdates.marketProjectId);
    benchmarkingFiltersContext.processSpecialties(primaryFilterUpdates.specialtyGroups);
    benchmarkingFiltersContext.setCountyIdFilter(primaryFilterUpdates.programCounties);
  }, [primaryFilterUpdates]);

  useEffect(() => {
    if (!!selectedSortType) {
      setSelectedSortType(null);
    }
  }, [networkFiltersLoading]);

  useEffect(() => {
    if (!!parentOrganizations?.length && !selectedSortType) {
      setSelectedSortType(MedicaidParentOrganizationSortingTypeEnum.AlphabeticalAsc);
    }
  }, [parentOrganizations]);

  useEffect(() => {
    if (!!applyingServiceArea.length) {
      onApplyPrimaryFiltersClick();
    } else {
      const countiesEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
        benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
      );

      const membershipCountEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload)?.membershipCount === selectedMembershipCount;
      const programEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload)?.programKey === benchmarkingFiltersContext.programKey;

      if ((!countiesEqual || !membershipCountEqual || !programEqual) && !!benchmarkingFiltersContext.selectedNetworks.length) {
        benchmarkingFiltersContext.setPreviousSelectedNetworks(cloneDeep(benchmarkingFiltersContext.selectedNetworks));
      }

      if (!!benchmarkingFiltersContext.displayedPrimaryFilters || !!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
        benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
        onClearNetworkFiltersClick();
      }
    }
  }, [
    benchmarkingFiltersContext.selectedState,
    benchmarkingFiltersContext.selectedCounties,
    selectedMembershipCount,
    benchmarkingFiltersContext.programKey,
  ]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
      // on network change, clear all displayed network filters
      benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
    }
  }, [benchmarkingFiltersContext.selectedNetworks]);

  const handleSetSelectedSortType = (sortType: CompetitiveInsightsSelectedSortType) => {
    setSelectedSortType(sortType as MedicaidParentOrganizationSortingTypeEnum);
  }

  const onApplyPrimaryFiltersClick = (): void => {
    const primaryFilters: MedicaidPrimaryFiltersPayload = {
      countyIds: benchmarkingFiltersContext.selectedCounties.map(x => parseInt(x)),
      lineOfBusinessId: benchmarkingFiltersContext.selectedLineOfBusiness,
      membershipCount: selectedMembershipCount,
      programKey: benchmarkingFiltersContext.programKey,
    };

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(cloneDeep(primaryFilters));

    applyPrimaryFiltersMutation({ applyingServiceArea, primaryFilters });
  }

  const onApplyServiceAreaClick = (countyIds: number[], contracts: BenchmarkingNetworkType[]): void => {
    const newSelectedCounties: BenchmarkingCounty[] = benchmarkingFiltersContext.rawCounties.filter(x => countyIds.includes(x.countyId));
    benchmarkingFiltersContext.setSelectedCounties(newSelectedCounties.map(x => x.countyId.toString()));
    setApplyingServiceArea(contracts as MedicaidNetwork[]);
  }

  const onClearPrimaryFiltersClick = (): void => {
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    benchmarkingFiltersContext.setCountyIdFilter(null);
    setSelectedMembershipCount(MembershipCountTypeEnum.All);
    benchmarkingFiltersContext.setProgramKey(null);
  }

  const onClearNetworkFiltersClick = (): void => {
    benchmarkingFiltersContext.setSelectedNetworks([]);
    benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
  }

  const sortNetworks = (filteredParents: BenchmarkingParentOrganizationType[]): BenchmarkingParentOrganizationType[] => {
    if (!!selectedSortType) {
      // "your qes networks" tile should be excluded from sorting and should always maintain the first position
      const medicaidParentOrganizations: MedicaidParentOrganization[] = filteredParents as MedicaidParentOrganization[];
      const yourQesNetworks = cloneDeep(medicaidParentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks"));
      let newSortedBenchmarkingParentOrganizations = cloneDeep(medicaidParentOrganizations.filter(x => x.parentOrganizationName !== "Your QES Networks"));

      switch (selectedSortType) {
        case MedicaidParentOrganizationSortingTypeEnum.AlphabeticalAsc:
          newSortedBenchmarkingParentOrganizations = orderBy(newSortedBenchmarkingParentOrganizations, (p) => p.parentOrganizationName, "asc");
          break;
        case MedicaidParentOrganizationSortingTypeEnum.AlphabeticalDesc:
          newSortedBenchmarkingParentOrganizations = orderBy(newSortedBenchmarkingParentOrganizations, (p) => p.parentOrganizationName, "desc");
          break;
        case MedicaidParentOrganizationSortingTypeEnum.MembershipCountAsc:
          newSortedBenchmarkingParentOrganizations = orderBy(newSortedBenchmarkingParentOrganizations, (p: MedicaidParentOrganization) => p.hContract.membershipCount, "asc")
          break;
        case MedicaidParentOrganizationSortingTypeEnum.MembershipCountDesc:
          newSortedBenchmarkingParentOrganizations = orderBy(newSortedBenchmarkingParentOrganizations, (p: MedicaidParentOrganization) => p.hContract.membershipCount, "desc")
          break;
        default:
          break;
      }

      filteredParents = !!yourQesNetworks ? [yourQesNetworks] : [];
      filteredParents = filteredParents.concat(newSortedBenchmarkingParentOrganizations);
    }

    return filteredParents;
  }

  return (
    <MedicaidFiltersContext.Provider
      value={{
        allPrograms,
        applyingServiceArea,
        handleSetSelectedSortType,
        networkFiltersLoading,
        onApplyPrimaryFiltersClick,
        onApplyServiceAreaClick,
        onClearNetworkFiltersClick,
        onClearPrimaryFiltersClick,
        parentOrganizations,
        primaryFiltersLoading,
        programs,
        selectedMembershipCount,
        selectedSortType,
        setSelectedMembershipCount,
        setSelectedSortType,
        sortNetworks,
      }}>
      {props.children}
    </MedicaidFiltersContext.Provider>
  )
}

export default MedicaidFiltersContextProvider;
